import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ClipboardList, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import { formatDate } from '../utils/datetime';
import HelpLink from '../components/HelpLink';

/**
 * System Tasks — the operator chore list the step-alert emails point at.
 *
 * These rows are written by SERVICES (workflow steps needing a human,
 * gate codes pending manual entry) — distinct from project work, which
 * has its own board. Until this page existed, 100 open chores had
 * writers but no reader. Oldest first: the top of the list is the thing
 * that has waited longest.
 */

const TYPE_LABELS = {
  workflow_step: 'Workflow step',
  gate_code_pending: 'Gate code pending',
  lead: 'Lead to work',
  // A rental on hold while a reported move-out is checked. Closes itself
  // when the rental is moved out or resumed.
  rental_paused: 'Verify move-out',
};

const typeChip = (t) => {
  const style = t === 'workflow_step'
    ? 'bg-orange-50 text-orange-600'
    : t === 'gate_code_pending'
      ? 'bg-blue-50 text-blue-600'
      : t === 'lead'
        ? 'bg-violet-50 text-violet-600'
        : t === 'rental_paused'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-slate-50 text-slate-500';
  return (
    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full whitespace-nowrap ${style}`}>
      {TYPE_LABELS[t] || t || 'other'}
    </span>
  );
};

const Tasks = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const [data, setData] = useState({ tasks: [], open_counts: {}, open_total: 0 });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const fetchTasks = useCallback(async () => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = typeFilter === 'all' ? {} : { type: typeFilter };
      const res = await apiClient.get(
        `/api/facilities/${facilityId}/system-tasks/`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load tasks.');
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading, typeFilter]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const completeAll = async () => {
    // Leads are read-time line items worked in Leads and Reservations —
    // bulk-complete only ever touches real task rows.
    const leadCount = data.open_counts.lead || 0;
    const n = typeFilter === 'all'
      ? data.open_total - leadCount
      : (data.open_counts[typeFilter] || 0);
    if (n === 0) return;
    const scope = typeFilter === 'all'
      ? `all ${n} open tasks`
      : `all ${n} open "${TYPE_LABELS[typeFilter] || typeFilter}" tasks`;
    if (!window.confirm(`Mark ${scope} done? There is no undo.`)) return;
    setBusyId('ALL');
    try {
      const params = typeFilter === 'all' ? {} : { type: typeFilter };
      const res = await apiClient.post(
        `/api/facilities/${facilityId}/system-tasks/complete-all`, null, { params });
      toast.success(`${res.data?.completed ?? 0} tasks marked done.`);
      fetchTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete the tasks.');
    } finally {
      setBusyId(null);
    }
  };

  const complete = async (t) => {
    setBusyId(t._id);
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/system-tasks/${t._id}/complete`);
      toast.success('Task marked done.');
      fetchTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete the task.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading || facilityLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const pills = [
    { key: 'all', label: `All (${data.open_total})` },
    ...Object.entries(data.open_counts).map(([k, n]) => ({
      key: k, label: `${TYPE_LABELS[k] || k} (${n})`,
    })),
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-slate-400" />
            Tasks <HelpLink slug="tasks" />
          </h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            {data.open_total} open — written by automations.
          </p>
        </div>
        {typeFilter !== 'lead'
          && (data.open_total - (data.open_counts.lead || 0)) > 0 && (
          <button type="button" onClick={completeAll} disabled={busyId === 'ALL'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-[12px] font-medium hover:bg-emerald-100 disabled:opacity-50 transition-colors">
            {busyId === 'ALL'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <CheckCircle2 className="h-3.5 w-3.5" />}
            Mark all done
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {pills.map((p) => (
          <button key={p.key} type="button" onClick={() => setTypeFilter(p.key)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              typeFilter === p.key
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        {data.tasks.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-slate-400">
            Nothing waiting — every automated chore is cleared.
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {data.tasks.map((t) => (
              <div key={t._id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {typeChip(t.type)}
                    <span className="text-[13px] font-medium text-slate-800">{t.title || '—'}</span>
                  </div>
                  {t.description && (
                    <p className="text-[12px] text-slate-500 mt-0.5 truncate" title={t.description}>
                      {t.description}
                    </p>
                  )}
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Opened {formatDate(t.created_at, currentFacility?.timezone) || '—'}
                  </p>
                </div>
                {t.type === 'lead' ? (
                  // A lead is WORKED, not checked off — it leaves this list
                  // when its status changes in Leads and Reservations.
                  <Link to="/reservations?tab=leads"
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 text-[12px] font-medium hover:bg-violet-100 transition-colors">
                    Open →
                  </Link>
                ) : (
                  <button type="button" onClick={() => complete(t)} disabled={busyId === t._id}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-[12px] font-medium hover:bg-emerald-100 disabled:opacity-50 transition-colors">
                    {busyId === t._id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Done
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tasks;
